import axios from 'axios';
import { API_BASE_URL } from '../config/api';
import { getClickIds, getAffiliateParams } from './tracking';

// Client half of the offer test (backend: utils/offerTest.js, read on
// /cro > Offer Testing).
//
// The variant lives in localStorage rather than a cookie because the journey
// starts on startviralgpt.com, whose host-only cookies never reach
// api.viralgpt.com — the same reason /pixel takes affiliate and fbclid in the
// body. The API also sets an `ov` cookie for the viralgpt.com half, but this
// store is the source of truth and is what gets sent back.
const KEY = 'ov';
const CONTROL = 'ov_a';

// Storage can throw in a private window, and a funnel test is never worth
// taking the page down with it.
const read = () => {
	try {
		return localStorage.getItem(KEY) || '';
	} catch (e) {
		return '';
	}
};
const write = (v) => {
	try {
		localStorage.setItem(KEY, v);
	} catch (e) {
		/* ignore */
	}
};

let cached = null;

// Resolves once per page load. Assigning is also what counts a visitor, so this
// is only allowed to assign when nothing is stored — a reload must not write a
// second 'view' row. Always resolves: on any failure the caller gets the
// control's tiers and the page renders as it does today.
export async function resolveVariant() {
	if (cached) return cached;
	const current = read();
	const clickIds = getClickIds();
	try {
		const res = await axios.get(`${API_BASE_URL}/offer/variant`, {
			params: {
				current: current || undefined,
				ad: getAffiliateParams().affiliate || undefined,
				clickId: clickIds.fbclid || clickIds.ttclid || undefined,
			},
			withCredentials: true,
		});
		const data = res.data || {};
		if (data.assigned && data.variant) write(data.variant);
		cached = {
			variant: data.variant || CONTROL,
			tiers: Array.isArray(data.tiers) && data.tiers.length ? data.tiers : null,
			prices: data.prices || {},
			inTest: !!data.inTest,
		};
	} catch (e) {
		cached = { variant: current || CONTROL, tiers: null, prices: {}, inTest: false };
	}
	return cached;
}

// checkout / submit / purchase. Fire-and-forget: the API answers 204 and a
// failure here must never surface in checkout.
// The arm, for the purchase body. Server-side oneTimeBaseFor() applies the same
// price override, so the charge matches the tier the page displayed.
export function currentVariant() {
	return (cached && cached.inTest && cached.variant) || null;
}

// Per-pack price override for the assigned arm, or null when it does not
// reprice anything.
export function variantPrice(pack) {
	const v = cached && cached.prices ? cached.prices[pack] : null;
	return typeof v === 'number' ? v : null;
}

export function logOfferEvent(event, extra = {}) {
	// Resolves rather than reading the cache, because an event can fire before
	// the assignment lands. The preselected-plan path starts checkout from a
	// mount effect that races the assignment call, and reading the cache
	// directly dropped that event silently. resolveVariant() caches, so this
	// costs one call per page load however many events follow it.
	resolveVariant()
		.then((v) => {
			if (!v || !v.inTest || !v.variant) return null;
			return axios.post(
				`${API_BASE_URL}/offer/event`,
				{ variant: v.variant, event, ...extra },
				{ withCredentials: true }
			);
		})
		.catch(() => {});
}
